import { useState, useMemo } from 'react';
import { useLang } from '../../../hooks/useLang';

interface Comment {
  id: string;
  author: string;
  avatar: string;
  content: string;
  time: string;
  likes: number;
  liked: boolean;
  replies?: Comment[];
}

// Localized mock comments per language
const LOCALIZED_COMMENTS: Record<string, Comment[]> = {
  zh: [
    {
      id: 'c1', author: '资产管理从业者', avatar: 'A',
      content: '清鉴先生这篇文章对八部委新政的解读角度非常独到，尤其是"合规是护城河而非成本"这个判断，切中要害。我们公司内部正在讨论RWA布局，转发学习了。',
      time: '2026-02-09 14:32', likes: 48, liked: false,
      replies: [{
        id: 'c1r1', author: '清鉴元衡', avatar: '清',
        content: '感谢认可。合规能力确实正在成为行业的新型竞争壁垒，先行布局的企业将在下一轮市场格局重塑中获得先发优势。欢迎继续探讨。',
        time: '2026-02-09 16:15', likes: 32, liked: false,
      }],
    },
    {
      id: 'c2', author: '律师同行', avatar: 'L',
      content: '关于境内确权存证的法律效力问题，文章分析得很准确。实操中我们也遇到过存证平台资质不符导致存证无效的案例，建议企业务必重视平台选择这一环节。',
      time: '2026-02-10 09:18', likes: 36, liked: false,
    },
    {
      id: 'c3', author: '绿能企业合规负责人', avatar: 'G',
      content: '文中关于绿色能源资产确权的操作细节很实用，我们公司光伏项目的环评报告问题就曾让整个确权流程拖延了两个月，希望先生能专门出一篇绿色能源RWA的深度分析。',
      time: '2026-02-12 11:45', likes: 24, liked: false,
    },
  ],
  en: [
    {
      id: 'c1', author: 'Asset Manager', avatar: 'A',
      content: 'This article offers a uniquely insightful perspective on RWA compliance frameworks. The argument that "compliance is a moat, not a cost" is spot on. We\'re currently discussing our RWA strategy internally — sharing this with the team.',
      time: '2026-02-09 14:32', likes: 48, liked: false,
      replies: [{
        id: 'c1r1', author: 'ChaInsight', avatar: 'C',
        content: 'Thank you for the kind words. Compliance capability is indeed becoming a new competitive barrier in the industry. Early movers will gain a significant first-mover advantage in the next market restructuring. Welcome to continue the discussion.',
        time: '2026-02-09 16:15', likes: 32, liked: false,
      }],
    },
    {
      id: 'c2', author: 'Legal Practitioner', avatar: 'L',
      content: 'The analysis on the legal validity of on-chain asset registration is very accurate. In practice, we\'ve encountered cases where notarization platform credentials didn\'t meet requirements, rendering the registration invalid. Companies must pay close attention to platform selection.',
      time: '2026-02-10 09:18', likes: 36, liked: false,
    },
    {
      id: 'c3', author: 'Green Energy Compliance Officer', avatar: 'G',
      content: 'The operational details on green energy asset tokenization are extremely practical. Our solar project\'s environmental assessment issues once delayed the entire registration process by two months. Hope to see a dedicated deep-dive on green energy RWA.',
      time: '2026-02-12 11:45', likes: 24, liked: false,
    },
  ],
  fr: [
    {
      id: 'c1', author: 'Gestionnaire d\'actifs', avatar: 'A',
      content: 'Cet article offre une perspective très originale sur les cadres de conformité RWA. L\'argument selon lequel "la conformité est un fossé, pas un coût" est particulièrement pertinent. Nous discutons actuellement de notre stratégie RWA en interne — je partage ceci avec l\'équipe.',
      time: '2026-02-09 14:32', likes: 48, liked: false,
      replies: [{
        id: 'c1r1', author: 'ChaInsight', avatar: 'C',
        content: 'Merci pour vos aimables paroles. La capacité de conformité devient effectivement une nouvelle barrière concurrentielle dans l\'industrie. Les pionniers obtiendront un avantage significatif lors de la prochaine restructuration du marché.',
        time: '2026-02-09 16:15', likes: 32, liked: false,
      }],
    },
    {
      id: 'c2', author: 'Juriste spécialisé', avatar: 'L',
      content: 'L\'analyse sur la validité juridique de l\'enregistrement des actifs on-chain est très précise. En pratique, nous avons rencontré des cas où les accréditations des plateformes de notarisation ne répondaient pas aux exigences. Les entreprises doivent accorder une grande attention au choix de la plateforme.',
      time: '2026-02-10 09:18', likes: 36, liked: false,
    },
    {
      id: 'c3', author: 'Responsable conformité énergie verte', avatar: 'G',
      content: 'Les détails opérationnels sur la tokenisation des actifs d\'énergie verte sont extrêmement pratiques. Les problèmes d\'évaluation environnementale de notre projet solaire ont retardé tout le processus d\'enregistrement de deux mois. J\'espère voir une analyse approfondie dédiée au RWA d\'énergie verte.',
      time: '2026-02-12 11:45', likes: 24, liked: false,
    },
  ],
  ja: [
    {
      id: 'c1', author: '資産運用担当者', avatar: 'A',
      content: 'この記事はRWAコンプライアンスフレームワークについて非常に独自の視点を提供しています。「コンプライアンスはコストではなく堀である」という主張は的を射ています。社内でRWA戦略を議論中ですが、チームと共有します。',
      time: '2026-02-09 14:32', likes: 48, liked: false,
      replies: [{
        id: 'c1r1', author: 'ChaInsight', avatar: 'C',
        content: 'ご評価いただきありがとうございます。コンプライアンス能力は確かに業界の新たな競争障壁になりつつあります。先行企業は次の市場再編において大きな先行者利益を得るでしょう。引き続きご議論をお待ちしております。',
        time: '2026-02-09 16:15', likes: 32, liked: false,
      }],
    },
    {
      id: 'c2', author: '法律専門家', avatar: 'L',
      content: 'オンチェーン資産登録の法的有効性に関する分析は非常に正確です。実務では、公証プラットフォームの資格が要件を満たさず登録が無効になったケースも経験しました。企業はプラットフォーム選択に十分注意する必要があります。',
      time: '2026-02-10 09:18', likes: 36, liked: false,
    },
    {
      id: 'c3', author: 'グリーンエネルギー企業コンプライアンス責任者', avatar: 'G',
      content: 'グリーンエネルギー資産のトークン化に関する実務的な詳細は非常に参考になります。当社の太陽光プロジェクトの環境評価問題で登録プロセス全体が2ヶ月遅延しました。グリーンエネルギーRWAの専門的な深掘り分析を期待しています。',
      time: '2026-02-12 11:45', likes: 24, liked: false,
    },
  ],
  es: [
    {
      id: 'c1', author: 'Gestor de activos', avatar: 'A',
      content: 'Este artículo ofrece una perspectiva muy original sobre los marcos de cumplimiento RWA. El argumento de que "el cumplimiento es un foso, no un costo" es muy acertado. Estamos discutiendo nuestra estrategia RWA internamente — comparto esto con el equipo.',
      time: '2026-02-09 14:32', likes: 48, liked: false,
      replies: [{
        id: 'c1r1', author: 'ChaInsight', avatar: 'C',
        content: 'Gracias por sus amables palabras. La capacidad de cumplimiento se está convirtiendo en una nueva barrera competitiva en la industria. Los pioneros obtendrán una ventaja significativa en la próxima reestructuración del mercado.',
        time: '2026-02-09 16:15', likes: 32, liked: false,
      }],
    },
    {
      id: 'c2', author: 'Profesional legal', avatar: 'L',
      content: 'El análisis sobre la validez legal del registro de activos on-chain es muy preciso. En la práctica, hemos encontrado casos donde las credenciales de las plataformas de notarización no cumplían los requisitos. Las empresas deben prestar mucha atención a la selección de plataformas.',
      time: '2026-02-10 09:18', likes: 36, liked: false,
    },
    {
      id: 'c3', author: 'Responsable de cumplimiento de energía verde', avatar: 'G',
      content: 'Los detalles operativos sobre la tokenización de activos de energía verde son extremadamente prácticos. Los problemas de evaluación ambiental de nuestro proyecto solar retrasaron todo el proceso de registro dos meses. Espero ver un análisis profundo dedicado al RWA de energía verde.',
      time: '2026-02-12 11:45', likes: 24, liked: false,
    },
  ],
  de: [
    {
      id: 'c1', author: 'Vermögensverwalter', avatar: 'A',
      content: 'Dieser Artikel bietet eine sehr originelle Perspektive auf RWA-Compliance-Frameworks. Das Argument, dass "Compliance ein Burggraben und keine Kosten ist", trifft den Nagel auf den Kopf. Wir diskutieren intern unsere RWA-Strategie — ich teile dies mit dem Team.',
      time: '2026-02-09 14:32', likes: 48, liked: false,
      replies: [{
        id: 'c1r1', author: 'ChaInsight', avatar: 'C',
        content: 'Vielen Dank für Ihre freundlichen Worte. Compliance-Fähigkeit wird tatsächlich zu einer neuen Wettbewerbsbarriere in der Branche. Frühe Akteure werden bei der nächsten Marktumstrukturierung einen erheblichen Vorteil erzielen.',
        time: '2026-02-09 16:15', likes: 32, liked: false,
      }],
    },
    {
      id: 'c2', author: 'Rechtsexperte', avatar: 'L',
      content: 'Die Analyse zur rechtlichen Gültigkeit der On-Chain-Asset-Registrierung ist sehr präzise. In der Praxis haben wir Fälle erlebt, in denen die Akkreditierungen der Notarisierungsplattformen die Anforderungen nicht erfüllten. Unternehmen müssen der Plattformauswahl große Aufmerksamkeit widmen.',
      time: '2026-02-10 09:18', likes: 36, liked: false,
    },
    {
      id: 'c3', author: 'Compliance-Beauftragter für grüne Energie', avatar: 'G',
      content: 'Die operativen Details zur Tokenisierung von Grünenergieaktiva sind äußerst praktisch. Die Umweltbewertungsprobleme unseres Solarprojekts haben den gesamten Registrierungsprozess um zwei Monate verzögert. Ich hoffe auf eine dedizierte Tiefenanalyse zum Thema Grüne Energie RWA.',
      time: '2026-02-12 11:45', likes: 24, liked: false,
    },
  ],
  ko: [
    {
      id: 'c1', author: '자산 관리자', avatar: 'A',
      content: '이 기사는 RWA 컴플라이언스 프레임워크에 대해 매우 독창적인 관점을 제공합니다. "컴플라이언스는 비용이 아닌 해자"라는 주장은 정확합니다. 내부적으로 RWA 전략을 논의 중인데 팀과 공유하겠습니다.',
      time: '2026-02-09 14:32', likes: 48, liked: false,
      replies: [{
        id: 'c1r1', author: 'ChaInsight', avatar: 'C',
        content: '좋은 말씀 감사합니다. 컴플라이언스 역량은 실제로 업계의 새로운 경쟁 장벽이 되고 있습니다. 선도 기업들은 다음 시장 재편에서 상당한 선점 우위를 얻을 것입니다. 계속 논의해 주세요.',
        time: '2026-02-09 16:15', likes: 32, liked: false,
      }],
    },
    {
      id: 'c2', author: '법률 전문가', avatar: 'L',
      content: '온체인 자산 등록의 법적 유효성에 관한 분석이 매우 정확합니다. 실무에서 공증 플랫폼 자격이 요건을 충족하지 못해 등록이 무효화된 사례를 경험했습니다. 기업들은 플랫폼 선택에 각별히 주의해야 합니다.',
      time: '2026-02-10 09:18', likes: 36, liked: false,
    },
    {
      id: 'c3', author: '그린에너지 기업 컴플라이언스 담당자', avatar: 'G',
      content: '그린에너지 자산 토큰화에 관한 실무적 세부 사항이 매우 유용합니다. 당사 태양광 프로젝트의 환경 평가 문제로 전체 등록 프로세스가 두 달 지연됐습니다. 그린에너지 RWA에 대한 전문적인 심층 분석을 기대합니다.',
      time: '2026-02-12 11:45', likes: 24, liked: false,
    },
  ],
  ar: [
    {
      id: 'c1', author: 'مدير أصول', avatar: 'A',
      content: 'تقدم هذه المقالة منظوراً فريداً حول أطر الامتثال لـ RWA. الحجة القائلة بأن "الامتثال هو خندق وليس تكلفة" دقيقة جداً. نحن نناقش استراتيجية RWA داخلياً — سأشارك هذا مع الفريق.',
      time: '2026-02-09 14:32', likes: 48, liked: false,
      replies: [{
        id: 'c1r1', author: 'ChaInsight', avatar: 'C',
        content: 'شكراً على كلماتك الطيبة. تصبح قدرة الامتثال بالفعل حاجزاً تنافسياً جديداً في الصناعة. ستحصل الشركات الرائدة على ميزة كبيرة في إعادة هيكلة السوق القادمة.',
        time: '2026-02-09 16:15', likes: 32, liked: false,
      }],
    },
    {
      id: 'c2', author: 'متخصص قانوني', avatar: 'L',
      content: 'التحليل المتعلق بالصحة القانونية لتسجيل الأصول على السلسلة دقيق جداً. في الممارسة العملية، واجهنا حالات لم تستوفِ فيها اعتمادات منصات التوثيق المتطلبات. يجب على الشركات الانتباه جيداً لاختيار المنصة.',
      time: '2026-02-10 09:18', likes: 36, liked: false,
    },
    {
      id: 'c3', author: 'مسؤول الامتثال في شركة الطاقة الخضراء', avatar: 'G',
      content: 'التفاصيل التشغيلية حول ترميز أصول الطاقة الخضراء مفيدة للغاية. أدت مشكلات التقييم البيئي لمشروعنا الشمسي إلى تأخير عملية التسجيل بأكملها شهرين. آمل في رؤية تحليل متعمق مخصص لـ RWA الطاقة الخضراء.',
      time: '2026-02-12 11:45', likes: 24, liked: false,
    },
  ],
  ru: [
    {
      id: 'c1', author: 'Управляющий активами', avatar: 'A',
      content: 'Эта статья предлагает очень оригинальный взгляд на системы соответствия RWA. Аргумент о том, что "соответствие — это ров, а не затраты", очень точен. Мы обсуждаем нашу стратегию RWA внутри компании — поделюсь этим с командой.',
      time: '2026-02-09 14:32', likes: 48, liked: false,
      replies: [{
        id: 'c1r1', author: 'ChaInsight', avatar: 'C',
        content: 'Спасибо за добрые слова. Способность к соответствию действительно становится новым конкурентным барьером в отрасли. Первопроходцы получат значительное преимущество при следующей реструктуризации рынка.',
        time: '2026-02-09 16:15', likes: 32, liked: false,
      }],
    },
    {
      id: 'c2', author: 'Юридический специалист', avatar: 'L',
      content: 'Анализ правовой действительности регистрации активов в блокчейне очень точен. На практике мы сталкивались со случаями, когда аккредитации нотариальных платформ не соответствовали требованиям. Компании должны уделять большое внимание выбору платформы.',
      time: '2026-02-10 09:18', likes: 36, liked: false,
    },
    {
      id: 'c3', author: 'Специалист по соответствию в сфере зеленой энергии', avatar: 'G',
      content: 'Операционные детали токенизации активов зеленой энергии чрезвычайно практичны. Проблемы экологической оценки нашего солнечного проекта задержали весь процесс регистрации на два месяца. Надеюсь увидеть специальный углубленный анализ RWA зеленой энергии.',
      time: '2026-02-12 11:45', likes: 24, liked: false,
    },
  ],
  pt: [
    {
      id: 'c1', author: 'Gestor de ativos', avatar: 'A',
      content: 'Este artigo oferece uma perspectiva muito original sobre os frameworks de conformidade RWA. O argumento de que "conformidade é um fosso, não um custo" é muito preciso. Estamos discutindo nossa estratégia RWA internamente — vou compartilhar isso com a equipe.',
      time: '2026-02-09 14:32', likes: 48, liked: false,
      replies: [{
        id: 'c1r1', author: 'ChaInsight', avatar: 'C',
        content: 'Obrigado pelas palavras gentis. A capacidade de conformidade está se tornando uma nova barreira competitiva na indústria. Os pioneiros obterão uma vantagem significativa na próxima reestruturação do mercado.',
        time: '2026-02-09 16:15', likes: 32, liked: false,
      }],
    },
    {
      id: 'c2', author: 'Especialista jurídico', avatar: 'L',
      content: 'A análise sobre a validade legal do registro de ativos on-chain é muito precisa. Na prática, encontramos casos em que as credenciais das plataformas de notarização não atendiam aos requisitos. As empresas devem prestar muita atenção à seleção de plataformas.',
      time: '2026-02-10 09:18', likes: 36, liked: false,
    },
    {
      id: 'c3', author: 'Responsável de conformidade de energia verde', avatar: 'G',
      content: 'Os detalhes operacionais sobre a tokenização de ativos de energia verde são extremamente práticos. Os problemas de avaliação ambiental do nosso projeto solar atrasaram todo o processo de registro em dois meses. Espero ver uma análise aprofundada dedicada ao RWA de energia verde.',
      time: '2026-02-12 11:45', likes: 24, liked: false,
    },
  ],
};

const COMMENT_UI_LABELS: Record<string, Record<string, string>> = {
  readerComments: { zh: '读者留言', en: 'Reader Comments', fr: 'Commentaires des lecteurs', es: 'Comentarios de lectores', pt: 'Comentários dos leitores', ja: '読者コメント', de: 'Leserkommentare', ko: '독자 댓글', ar: 'تعليقات القراء', ru: 'Комментарии читателей' },
  insight: { zh: '您的见解是本栏目最宝贵的财富，欢迎留下您的思考', en: 'Your insights are the most valuable asset of this column', fr: 'Vos idées sont l\'atout le plus précieux de cette colonne', es: 'Sus ideas son el activo más valioso de esta columna', pt: 'Suas ideias são o ativo mais valioso desta coluna', ja: 'あなたの洞察はこのコラムの最も貴重な財産です', de: 'Ihre Erkenntnisse sind das wertvollste Gut dieser Kolumne', ko: '귀하의 통찰력은 이 칼럼의 가장 소중한 자산입니다', ar: 'أفكارك هي أثمن أصول هذا العمود', ru: 'Ваши идеи — самый ценный актив этой колонки' },
  namePlaceholder: { zh: '您的称谓（选填）', en: 'Your name (optional)', fr: 'Votre nom (optionnel)', es: 'Su nombre (opcional)', pt: 'Seu nome (opcional)', ja: 'お名前（任意）', de: 'Ihr Name (optional)', ko: '이름 (선택)', ar: 'اسمك (اختياري)', ru: 'Ваше имя (необязательно)' },
  commentPlaceholder: { zh: '留下您的真知灼见...（字数不限）', en: 'Share your thoughts...', fr: 'Partagez vos réflexions...', es: 'Comparta sus pensamientos...', pt: 'Compartilhe seus pensamentos...', ja: 'あなたの考えを共有してください...', de: 'Teilen Sie Ihre Gedanken...', ko: '생각을 공유해 주세요...', ar: 'شارك أفكارك...', ru: 'Поделитесь своими мыслями...' },
  submitted: { zh: '评论已提交，感谢您的参与', en: 'Comment submitted, thank you!', fr: 'Commentaire soumis, merci!', es: '¡Comentario enviado, gracias!', pt: 'Comentário enviado, obrigado!', ja: 'コメントを送信しました、ありがとうございます！', de: 'Kommentar eingereicht, danke!', ko: '댓글이 제출되었습니다, 감사합니다!', ar: 'تم إرسال التعليق، شكراً!', ru: 'Комментарий отправлен, спасибо!' },
  postComment: { zh: '发表评论', en: 'Post Comment', fr: 'Publier', es: 'Publicar', pt: 'Publicar', ja: 'コメントを投稿', de: 'Kommentieren', ko: '댓글 작성', ar: 'نشر تعليق', ru: 'Опубликовать' },
  author: { zh: '作者', en: 'Author', fr: 'Auteur', es: 'Autor', pt: 'Autor', ja: '著者', de: 'Autor', ko: '작성자', ar: 'المؤلف', ru: 'Автор' },
  reply: { zh: '回复', en: 'Reply', fr: 'Répondre', es: 'Responder', pt: 'Responder', ja: '返信', de: 'Antworten', ko: '답글', ar: 'رد', ru: 'Ответить' },
  replyPlaceholder: { zh: '回复...', en: 'Reply...', fr: 'Répondre...', es: 'Responder...', pt: 'Responder...', ja: '返信...', de: 'Antworten...', ko: '답글...', ar: 'رد...', ru: 'Ответить...' },
  cancel: { zh: '取消', en: 'Cancel', fr: 'Annuler', es: 'Cancelar', pt: 'Cancelar', ja: 'キャンセル', de: 'Abbrechen', ko: '취소', ar: 'إلغاء', ru: 'Отмена' },
  send: { zh: '发送', en: 'Send', fr: 'Envoyer', es: 'Enviar', pt: 'Enviar', ja: '送信', de: 'Senden', ko: '전송', ar: 'إرسال', ru: 'Отправить' },
  anonymousReader: { zh: '匿名读者', en: 'Anonymous Reader', fr: 'Lecteur anonyme', es: 'Lector anónimo', pt: 'Leitor anônimo', ja: '匿名読者', de: 'Anonymer Leser', ko: '익명 독자', ar: 'قارئ مجهول', ru: 'Анонимный читатель' },
  reader: { zh: '读者', en: 'Reader', fr: 'Lecteur', es: 'Lector', pt: 'Leitor', ja: '読者', de: 'Leser', ko: '독자', ar: 'قارئ', ru: 'Читатель' },
};

// Author name per language
const AUTHOR_NAME: Record<string, string> = {
  zh: '清鉴元衡', en: 'ChaInsight', fr: 'ChaInsight', es: 'ChaInsight', pt: 'ChaInsight',
  ja: 'ChaInsight', de: 'ChaInsight', ko: 'ChaInsight', ar: 'ChaInsight', ru: 'ChaInsight',
};

interface CommentSectionProps {
  articleId: string;
  commentCount: number;
}

export default function CommentSection({ articleId: _articleId, commentCount }: CommentSectionProps) {
  const { lang } = useLang();
  const getLabel = (key: string) => COMMENT_UI_LABELS[key]?.[lang] ?? COMMENT_UI_LABELS[key]?.['en'] ?? key;
  const authorName = AUTHOR_NAME[lang] ?? 'ChaInsight';

  const baseComments = useMemo(() => {
    const localized = LOCALIZED_COMMENTS[lang] ?? LOCALIZED_COMMENTS['en'];
    return localized;
  }, [lang]);

  const [comments, setComments] = useState<Comment[]>(baseComments);
  const [newComment, setNewComment] = useState('');
  const [author, setAuthor] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Reset comments when language changes
  const [prevLang, setPrevLang] = useState(lang);
  if (prevLang !== lang) {
    setPrevLang(lang);
    setComments(LOCALIZED_COMMENTS[lang] ?? LOCALIZED_COMMENTS['en']);
  }

  const handleLike = (id: string, isReply?: boolean, parentId?: string) => {
    if (isReply && parentId) {
      setComments(prev => prev.map(c => {
        if (c.id === parentId && c.replies) {
          return { ...c, replies: c.replies.map(r => r.id === id ? { ...r, liked: !r.liked, likes: r.liked ? r.likes - 1 : r.likes + 1 } : r) };
        }
        return c;
      }));
    } else {
      setComments(prev => prev.map(c => c.id === id ? { ...c, liked: !c.liked, likes: c.liked ? c.likes - 1 : c.likes + 1 } : c));
    }
  };

  const handleSubmit = () => {
    if (!newComment.trim()) return;
    const comment: Comment = {
      id: `c${Date.now()}`,
      author: author.trim() || getLabel('anonymousReader'),
      avatar: (author.trim() || 'A')[0].toUpperCase(),
      content: newComment.trim(),
      time: new Date().toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
      likes: 0, liked: false,
    };
    setComments(prev => [comment, ...prev]);
    setNewComment(''); setAuthor('');
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  const handleReply = (commentId: string) => {
    if (!replyContent.trim()) return;
    const reply: Comment = {
      id: `r${Date.now()}`,
      author: getLabel('reader'),
      avatar: 'R',
      content: replyContent.trim(),
      time: new Date().toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }),
      likes: 0, liked: false,
    };
    setComments(prev => prev.map(c => c.id === commentId ? { ...c, replies: [...(c.replies || []), reply] } : c));
    setReplyTo(null); setReplyContent('');
  };

  return (
    <section className="mt-16 border-t border-gray-100 pt-12">
      <h3 className="text-xl font-bold text-gray-900 mb-8 flex items-center gap-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-discuss-line text-[#7C3AED]"></i>
        </div>
        {getLabel('readerComments')}
        <span className="text-sm font-normal text-gray-400 ml-2">({commentCount + comments.length - baseComments.length})</span>
      </h3>

      {/* Comment Input */}
      <div className="bg-gray-50 rounded-xl p-6 mb-10 border border-gray-100">
        <p className="text-sm text-gray-500 mb-4">{getLabel('insight')}</p>
        <input
          type="text"
          placeholder={getLabel('namePlaceholder')}
          value={author}
          onChange={e => setAuthor(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm mb-3 focus:outline-none focus:border-[#7C3AED] bg-white"
        />
        <textarea
          placeholder={getLabel('commentPlaceholder')}
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          rows={4}
          className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm resize-none focus:outline-none focus:border-[#7C3AED] bg-white"
        />
        <div className="flex justify-between items-center mt-3">
          {submitted ? (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <i className="ri-check-line"></i>{getLabel('submitted')}
            </span>
          ) : <span></span>}
          <button
            onClick={handleSubmit}
            disabled={!newComment.trim()}
            className="px-6 py-2 bg-[#7C3AED] text-white text-sm font-medium rounded-lg hover:bg-[#6D28D9] transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {getLabel('postComment')}
          </button>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-8">
        {comments.map(comment => (
          <div key={comment.id} className="group">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 bg-gradient-to-br from-[#7C3AED] to-[#A78BFA]">
                {comment.avatar}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1.5">
                  <span className={`text-sm font-semibold ${comment.author === authorName ? 'text-[#7C3AED]' : 'text-gray-800'}`}>
                    {comment.author}
                    {comment.author === authorName && (
                      <span className="ml-2 text-xs bg-[#7C3AED]/10 text-[#7C3AED] px-2 py-0.5 rounded-full">{getLabel('author')}</span>
                    )}
                  </span>
                  <span className="text-xs text-gray-400">{comment.time}</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{comment.content}</p>
                <div className="flex items-center gap-4 mt-2">
                  <button
                    onClick={() => handleLike(comment.id)}
                    className={`flex items-center gap-1 text-xs transition-colors cursor-pointer ${comment.liked ? 'text-[#7C3AED]' : 'text-gray-400 hover:text-[#7C3AED]'}`}
                  >
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={comment.liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}></i>
                    </div>
                    {comment.likes}
                  </button>
                  <button
                    onClick={() => setReplyTo(replyTo === comment.id ? null : comment.id)}
                    className="text-xs text-gray-400 hover:text-[#7C3AED] transition-colors cursor-pointer"
                  >
                    {getLabel('reply')}
                  </button>
                </div>

                {replyTo === comment.id && (
                  <div className="mt-4 bg-gray-50 rounded-lg p-4">
                    <textarea
                      placeholder={getLabel('replyPlaceholder')}
                      value={replyContent}
                      onChange={e => setReplyContent(e.target.value)}
                      rows={3}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-[#7C3AED] bg-white"
                    />
                    <div className="flex gap-2 mt-2 justify-end">
                      <button onClick={() => setReplyTo(null)} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap">
                        {getLabel('cancel')}
                      </button>
                      <button
                        onClick={() => handleReply(comment.id)}
                        className="px-4 py-1.5 bg-[#7C3AED] text-white text-xs rounded-lg hover:bg-[#6D28D9] cursor-pointer whitespace-nowrap"
                      >
                        {getLabel('send')}
                      </button>
                    </div>
                  </div>
                )}

                {comment.replies && comment.replies.length > 0 && (
                  <div className="mt-4 ml-4 border-l-2 border-[#7C3AED]/20 pl-4 space-y-4">
                    {comment.replies.map(reply => (
                      <div key={reply.id} className="flex gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0 bg-gradient-to-br from-[#7C3AED] to-[#A78BFA]">
                          {reply.avatar}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-sm font-semibold ${reply.author === authorName ? 'text-[#7C3AED]' : 'text-gray-800'}`}>
                              {reply.author}
                              {reply.author === authorName && (
                                <span className="ml-1 text-xs bg-[#7C3AED]/10 text-[#7C3AED] px-1.5 py-0.5 rounded-full">{getLabel('author')}</span>
                              )}
                            </span>
                            <span className="text-xs text-gray-400">{reply.time}</span>
                          </div>
                          <p className="text-sm text-gray-700 leading-relaxed">{reply.content}</p>
                          <button
                            onClick={() => handleLike(reply.id, true, comment.id)}
                            className={`flex items-center gap-1 text-xs mt-1.5 transition-colors cursor-pointer ${reply.liked ? 'text-[#7C3AED]' : 'text-gray-400 hover:text-[#7C3AED]'}`}
                          >
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className={reply.liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}></i>
                            </div>
                            {reply.likes}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
